<template>
  <alert
    v-if="uploadError"
    :message="uploadError"
    @dismiss="uploadError = null"
  />

  <small
    v-if="isUploading"
    class="form-text text-muted"
    role="status"
  >
    {{ uploadProgressMessage }}
  </small>

  <div
    ref="rootEl"
    :class="['upload-field input-group', inputGroupClass]"
  >
    <input
      :value="file.filename"
      type="text"
      class="form-control file-name"
      readonly
    >
    <!-- @vue-generic { UploadResponse } -->
    <upload-button
      :title="uploadButtonTitle"
      :upload-url="uploadUrl"
      @upload-error="onUploadError"
      @upload-progress="onUploadProgress"
      @uploaded="onFileUploaded"
    />
    <button
      type="button"
      class="btn btn-primary dropdown-toggle dropdown-toggle-split"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      :disabled="isUploading"
    >
      <span class="visually-hidden">Toggle Dropdown</span>
    </button>
    <div class="dropdown-menu dropdown-menu-end">
      <a :class="{'dropdown-item': true, disabled: !hasFile || isUploading}" target="_blank" :href="file.url">{{ $t('upload_field.download') }}</a>
      <a :class="{'dropdown-item': true, disabled: !hasFile || isUploading}" href="#" @click.prevent="showRemoveFilePrompt">{{ $t('upload_field.remove') }}</a>
    </div>

    <teleport
      to="#vue-portal-target"
      append
    >
      <modal-confirmation-rails
        ref="removeFileConfirmationModal"
        @confirmed="onRemoveFileConfirmed"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef, watch} from "vue"
import {useTranslation} from "i18next-vue"

import Alert from "./alert.vue"
import UploadButton from "./upload-button.vue"
import ModalConfirmationRails from "./modal-confirmation-rails.vue"
import {isNonEmptyString} from "@/js/js-utils"

defineProps({
  uploadUrl: {default: null, type: String},
  inputGroupClass: {default: null, type: String},
})

interface UploadFile {
  filename: string
  id: string
  url?: string
}

interface UploadResponse {
  file: UploadFile
}

const {t: $t} = useTranslation()

const rootEl = useTemplateRef<HTMLElement>("rootEl")
const removeFileConfirmationModal = useTemplateRef<InstanceType<typeof ModalConfirmationRails>>("removeFileConfirmationModal")
const valueField = ref<HTMLInputElement | null>(null)
const file = defineModel<UploadFile>({
  default: () => ({
    filename: "",
    url: "",
    id: "",
  })
})
const isUploading = ref(false)
const uploadProgressMessage = ref("")
const uploadError = ref<string | null>(null)

const hasFile = computed((): boolean => {
  return isNonEmptyString(file.value.id)
})

const uploadButtonTitle = computed((): string => {
  return hasFile.value ? $t('upload_field.replace') : $t('upload_field.add')
})

const onFileUploaded = (data: UploadResponse): void => {
  file.value = data.file
  uploadError.value = null
}

const onUploadError = (error: string): void => {
  isUploading.value = false
  uploadError.value = error
}

const onUploadProgress = (uploading: boolean, progress?: number, message?: string): void => {
  isUploading.value = uploading
  uploadProgressMessage.value = message || ''
  if (uploading && typeof progress === 'number' && progress > 0 && progress < 100) {
    uploadProgressMessage.value = `${message || $t('upload_field.uploading')} ${progress} %`
  }
}

const showRemoveFilePrompt = (): void => {
  removeFileConfirmationModal.value?.show(null, {
    title: $t('upload_field.remove_confirmation.title'),
    message: $t('upload_field.remove_confirmation.message'),
    type: 'danger',
    cancelActionTitle: $t('upload_field.remove_confirmation.cancel'),
    confirmActionTitle: $t('upload_field.remove_confirmation.confirm'),
    confirmationCode: null,
  })
}

const onRemoveFileConfirmed = (): void => {
  removeFileConfirmationModal.value?.hide()
  file.value = {
    filename: "",
    url: "",
    id: "",
  }
  uploadError.value = null
}

watch(file, (value): void => {
  if (valueField.value) {
    valueField.value.value = JSON.stringify({
      id: value.id,
      filename: value.filename,
      url: value.url
    })
  }
}, {deep: true})

onMounted(() => {
  const valEl = rootEl.value?.parentElement?.parentElement?.querySelector('input[ref="fileId"]')
  if (valEl instanceof HTMLInputElement) {
    valueField.value = valEl
    if (isNonEmptyString(valueField.value.value)) {
      try {
        const defaultValue = JSON.parse(valueField.value.value) as UploadFile
        if (defaultValue && typeof defaultValue === 'object') {
          file.value = {
            id: defaultValue.id || '',
            filename: defaultValue.filename || '',
            url: defaultValue.url || '',
          }
        }
      } catch {
        // pass
      }
    }
  }
})
</script>
